/**
 * Configuration for toggleable tools by agent mode.
 * These tools are always displayed for Admin/AIEngineer users
 * and can be toggled on/off directly from the chat interface.
 */

// Agentic cycle states: 0 = No agentic, 1 = Agentic, 2 = Agentic 2.0 (Interleaved)
export const AGENTIC_CYCLE_STATES = {
  NONE: 0,
  AGENTIC: 1,
  AGENTIC_INTERLEAVED: 2,
};

export const AGENTIC_CYCLE_CONFIG = {
  [AGENTIC_CYCLE_STATES.NONE]: {
    icon: "agenticInterleaved",
    description: "No agentic",
    color: "mediumGrey",
  },
  [AGENTIC_CYCLE_STATES.AGENTIC]: {
    icon: "agentic",
    description: "Agentic 1.0",
    color: "orange",
  },
  [AGENTIC_CYCLE_STATES.AGENTIC_INTERLEAVED]: {
    icon: "agenticInterleaved",
    description: "Agentic 2.0",
    color: "orange",
  },
};

export const getAgenticCycleState = (agent) => {
  if (agent?.useInterleavedReasoning)
    return AGENTIC_CYCLE_STATES.AGENTIC_INTERLEAVED;
  if (agent?.agenticRAG) return AGENTIC_CYCLE_STATES.AGENTIC;
  return AGENTIC_CYCLE_STATES.NONE;
};

export const getNextAgenticCycleState = (currentState) => {
  if (currentState === 0) return AGENTIC_CYCLE_STATES.AGENTIC_INTERLEAVED;
  return AGENTIC_CYCLE_STATES.NONE;
};

// Maps agent mode to toggleable tool configurations
export const TOGGLEABLE_TOOLS_BY_MODE = {
  retriever: [
    {
      property: "agenticCycle",
      icon: "agenticInterleaved",
      description: "No agentic",
      isCycleToggle: true,
    },
    {
      property: "hasCode",
      icon: "code",
      description: "Code execution",
    },
    {
      property: "allowInternetSearch",
      icon: "internet",
      description: "Internet search",
    },
    {
      property: "allowDeepInternetSearch",
      icon: "deep_search",
      description: "Deep search",
    },
    {
      property: "allowIntrospection",
      icon: "introspection",
      description: "Agent Introspection",
    },
    {
      property: "charting",
      icon: "charting",
      description: "Charting",
    },
    {
      property: "allowIntrospection",
      icon: "introspection",
      description: "Agent Introspection",
    },
    {
      property: "charting",
      icon: "charting",
      description: "Charting",
    },
  ],
  coder: [
    {
      property: "hasCode",
      icon: "code",
      description: "Code execution",
    },
  ],
  chatter: [
    {
      property: "allowImagesGeneration",
      icon: "image-gen",
      description: "Image generation",
    },
    {
      property: "allow3dModelGeneration",
      icon: "3d-gen",
      description: "3D model generation",
    },
    {
      property: "allowVideosGeneration",
      icon: "video-gen",
      description: "Video generation",
    },
  ],
};

/**
 * Defines cascading disable rules.
 * When a property is disabled, all properties in its array should also be disabled.
 */
export const CASCADING_DISABLE_RULES = {
  agenticRAG: ["hasCode"],
};

/**
 * Get the list of toggleable tools for a given agent mode
 * @param {string} mode - Agent mode (retriever, coder, chatter, etc.)
 * @returns {Array} Array of toggleable tool configurations
 */
export const getToggleableToolsForMode = (mode) => {
  return TOGGLEABLE_TOOLS_BY_MODE[mode] || [];
};

/**
 * Check if a tool property is toggleable for the given agent mode
 * @param {string} mode - Agent mode
 * @param {string} property - Tool property name
 * @returns {boolean} True if the tool is toggleable
 */
export const isToolToggleable = (mode, property) => {
  const toggleableTools = TOGGLEABLE_TOOLS_BY_MODE[mode] || [];
  return toggleableTools.some((tool) => tool.property === property);
};

/**
 * Get all toggleable property names for a mode
 * @param {string} mode - Agent mode
 * @returns {Array<string>} Array of property names
 */
export const getToggleablePropertyNames = (mode) => {
  const toggleableTools = TOGGLEABLE_TOOLS_BY_MODE[mode] || [];
  return toggleableTools.map((tool) => tool.property);
};

/**
 * Get properties that should be cascaded disabled when a property is disabled
 * @param {string} property - Property being disabled
 * @returns {Array<string>} Array of property names to also disable
 */
export const getCascadingDisables = (property) => {
  return CASCADING_DISABLE_RULES[property] || [];
};

export default {
  TOGGLEABLE_TOOLS_BY_MODE,
  CASCADING_DISABLE_RULES,
  getToggleableToolsForMode,
  isToolToggleable,
  getToggleablePropertyNames,
  getCascadingDisables,
  AGENTIC_CYCLE_STATES,
  AGENTIC_CYCLE_CONFIG,
  getAgenticCycleState,
  getNextAgenticCycleState,
};
